import {
  approveMobilePushOperation,
  cancelMobilePushOperation,
  fetchMobilePushOperation,
  rejectMobilePushOperation,
} from 'admin/actions/mobile-push-operations';
import { Item, Label, Row, LabeledTable as Table } from 'admin/components/molecules/LabeledTable';
import MobilePushOperation from 'admin/models/mobile-push-operation';
import { formatDate } from 'admin/utils';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { bindActionCreators } from 'redux';
import styled, { css } from 'styled-components';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import { createBadge } from '../../atoms/Badge';
import Button from '../../atoms/Buttons';
import CopyToClipboard from '../../atoms/CopyToClipboard';
import FA from '../../atoms/FontAwesome';
import Headings from '../../atoms/Headings';
import BreadCrumb from '../../molecules/BreadCrumb';

const PageWrapper = styled.div``;

const CopyTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 40px;
`;

const ModalWarningMessage = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;

  p {
    margin-bottom: 0;
    color: #d75f48;
  }
`;

const Icon = styled(FA)`
  font-size: 16px;
`;

const RejectButton = styled(Button)`
  ${({ theme, disabled }) => css`
    border-color: ${theme.colors.red[300]};
    color: ${theme.colors.red[300]};
    opacity: ${disabled ? '0.5' : '1'};
  `}
`;

const getCrumbs = (role) => [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '販促管理', href: '', isCurrentPage: true },
  {
    value: 'プッシュメッセージ',
    href:
      role === 'SERVICE-MANAGER'
        ? '/service-settings/all-mobile-push-operations'
        : '/promotion/mobile-push-operations',
    isCurrentPage: false,
  },
  { value: 'プッシュメッセージ詳細', href: '', isCurrentPage: true },
];

const targetRule = {
  none: '一部',
  'private-money': '全員',
};

const MobilePushOperationStatusBadge = createBadge({
  draft: { label: '下書き', bgColor: '#585f63' },
  submitted: { label: '申請済み', bgColor: '#3c86c9' },
  preprocessing: { label: '準備中', bgColor: '#3c86c9' },
  pending: { label: '承認待ち', bgColor: '#3c86c9' },
  approved: { label: '実行待ち', bgColor: '#3c86c9' },
  rejected: { label: '却下', bgColor: '#343a40' },
  processing: { label: '実行中', bgColor: '#c89600' },
  successed: { label: '完了', bgColor: '#208637' },
  error: { label: 'エラー', bgColor: '#a51d2a' },
  canceled: { label: 'キャンセル済み', bgColor: '#585f63' },
});

const MobilePushOperationDetail = ({
  user,
  operationId,
  fetchMobilePushOperation,
  openAlertModal,
  approveMobilePushOperation,
  rejectMobilePushOperation,
}) => {
  const [operation, setOperation] = useState({});
  const history = useHistory();
  useEffect(() => {
    fetchMobilePushOperation(operationId).then((o) =>
      setOperation(Object.assign(new MobilePushOperation(), o))
    );
  }, []);

  const onApproveOperation = () => {
    openAlertModal({
      body: (
        <div>
          <p>プッシュメッセージの配信を承認してよろしいですか</p>
          <ModalWarningMessage>
            <Icon icon="exclamation-triangle" color="#D75F48" />
            <p>この操作は元に戻せません</p>
          </ModalWarningMessage>
        </div>
      ),
      confirmText: '承認',
      onConfirm: () => {
        approveMobilePushOperation(operation.id).then(() =>
          history.push('/service-settings/all-mobile-push-operations', {
            completedOperation: 'approval',
          })
        );
      },
    });
  };

  const onRejectOperation = () => {
    openAlertModal({
      body: (
        <div>
          <p>プッシュメッセージの配信を却下してよろしいですか</p>
          <ModalWarningMessage>
            <Icon icon="exclamation-triangle" color="#D75F48" />
            <p>この操作は元に戻せません</p>
          </ModalWarningMessage>
        </div>
      ),
      confirmText: '却下',
      confirmColor: 'danger',
      onConfirm: () => {
        rejectMobilePushOperation(operation.id).then(() =>
          history.push('/service-settings/all-mobile-push-operations', {
            completedOperation: 'rejection',
          })
        );
      },
    });
  };

  return (
    <PageWrapper>
      <BreadCrumb crumbs={getCrumbs(user.role)} />
      <Headings.h5>プッシュメッセージ詳細</Headings.h5>
      <Table style={{ marginTop: '10px' }}>
        <Row>
          <Label>ID</Label>
          <CopyTextWrapper>
            <Item>{operation.id}</Item>
            <CopyToClipboard text={operation.id} />
          </CopyTextWrapper>
        </Row>
        <Row>
          <Label>状態</Label>
          <Item>
            <MobilePushOperationStatusBadge status={operation.status} />
          </Item>
        </Row>
        <Row>
          <Label>件名</Label>
          <Item>{operation.subject}</Item>
        </Row>
        <Row>
          <Label>本文</Label>
          <Item>{operation.message}</Item>
        </Row>
        <Row>
          <Label>配信希望日時</Label>
          <Item>{formatDate(operation.scheduled_at) || '-'}</Item>
        </Row>
        <Row>
          <Label>完了日時</Label>
          <Item>{formatDate(operation.done_at) || '-'}</Item>
        </Row>
        <Row>
          <Label>送信先</Label>
          <Item>{targetRule[operation.target_rule]}</Item>
        </Row>
        <Row>
          <Label>配信数</Label>
          <Item>{operation.users_count || '-'}</Item>
        </Row>
        <Row>
          <Label>閲覧数</Label>
          <Item>{operation.read_messages_count || 0}</Item>
        </Row>
        <Row>
          <Label>作成日時</Label>
          <Item>{formatDate(operation.created_at)}</Item>
        </Row>
        <Row>
          <Label>作成者</Label>
          <Item>{operation.operator && operation.operator.name}</Item>
        </Row>
      </Table>
      {user.isServiceUser() &&
      (operation?.status === 'pending' || operation?.status === 'submitted') ? (
        <ActionsWrapper>
          <RejectButton
            variant="outline"
            disabled={operation.status !== 'pending'}
            onClick={onRejectOperation}
          >
            却下
          </RejectButton>
          <Button
            onClick={onApproveOperation}
            disabled={operation.status !== 'pending'}
            style={{ backgroundColor: '#00A23F', color: '#FFF' }}
          >
            承認
          </Button>
        </ActionsWrapper>
      ) : null}
    </PageWrapper>
  );
};

const MobilePushOperationDetailContainer = (props) => (
  <MobilePushOperationDetail operationId={props.match.params.id} {...props} />
);

const mapStateToProps = (state) => ({
  user: state.user,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchMobilePushOperation,
      cancelMobilePushOperation,
      openAlertModal,
      approveMobilePushOperation,
      rejectMobilePushOperation,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(MobilePushOperationDetailContainer);
